import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, FileText, AlertTriangle, Scale, Lock } from 'lucide-react';

export const Legal: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'terms';

  const tabs = [
    { id: 'terms', label: 'Terms of Service', icon: FileText },
    { id: 'privacy', label: 'Privacy Policy', icon: Lock },
    { id: 'refund', label: '72-Hour Warranty & Refunds', icon: ShieldCheck },
    { id: 'rules', label: 'Marketplace Rules', icon: Scale },
    { id: 'abuse', label: 'Abuse & Infringement Reporting', icon: AlertTriangle },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      <div>
        <h1 className="text-3xl font-black text-brand-lightText dark:text-brand-darkText">
          Legal & Operational Policies
        </h1>
        <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
          Review our rules, consumer protection policies, and terms of service
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-brand-lightBorder dark:border-brand-darkBorder pb-4">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = currentTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSearchParams({ tab: t.id })}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all ${
                isActive
                  ? 'bg-brand-blue text-white shadow-glow-blue'
                  : 'bg-brand-lightSec dark:bg-brand-darkCard text-brand-lightSecText hover:text-brand-lightText'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-6 text-xs text-brand-lightText dark:text-brand-darkText leading-relaxed">
        {currentTab === 'terms' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">GameVault Terms of Service</h2>
            <p>Welcome to GameVault. By accessing or using our marketplace platform, you agree to be bound by these Terms of Service.</p>
            <h3 className="font-bold text-sm pt-2">1. Nature of the Marketplace</h3>
            <p>GameVault provides an intermediary digital platform connecting independent gaming goods sellers, professional boosters, and buyers. GameVault facilitates payment settlement, atomic stock delivery, and dispute mediation.</p>
            <h3 className="font-bold text-sm pt-2">2. User Accounts & Responsibilities</h3>
            <p>Users must provide accurate registration details. Users are strictly prohibited from sharing login credentials with unauthorized third parties.</p>
            <h3 className="font-bold text-sm pt-2">3. Publisher Policies & Disclaimers</h3>
            <p>Video game publishers (including Riot Games, Valve, Blizzard, and HoYoverse) maintain independent terms of service regarding virtual items and account transfers. GameVault provides realistic protection mechanisms but does not claim endorsement by any video game copyright owner.</p>
          </div>
        )}

        {currentTab === 'privacy' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Privacy & Data Protection Policy</h2>
            <p>GameVault respects your privacy and ensures rigorous data minimization across all Cloudflare edge networks.</p>
            <h3 className="font-bold text-sm pt-2">1. Information Collection & Worker Processing</h3>
            <p>We collect essential operational data: email addresses, hashed passwords, order records, and conversation history. Financial credit card numbers and CVV codes are never stored on GameVault servers.</p>
            <h3 className="font-bold text-sm pt-2">2. Cryptographic Protection of Digital Secrets</h3>
            <p>Stocked digital codes and account credentials are encrypted with AES-GCM-256 before storage in Cloudflare D1. Private encryption keys are maintained exclusively in worker runtime environment secrets.</p>
          </div>
        )}

        {currentTab === 'refund' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">72-Hour Buyer Protection & Refund Policy</h2>
            <p className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-medium">
              Every purchase of verified accounts, keys, or services on GameVault is backed by our strict 72-Hour Warranty.
            </p>
            <h3 className="font-bold text-sm pt-2">1. Covered Incidents</h3>
            <ul className="list-disc pl-5 space-y-1 text-brand-lightSecText">
              <li>Delivered activation keys that report "Already Redeemed" upon initial receipt.</li>
              <li>Account credentials that do not match advertised ranks, skins, or server region.</li>
              <li>Accounts that experience recovery or lock within the 72-hour warranty period.</li>
            </ul>
            <h3 className="font-bold text-sm pt-2">2. Dispute Mediation Workflow</h3>
            <p>Buyers can trigger a formal dispute directly from the order page. The seller has 24 hours to provide working replacements or evidence. If unresolved, GameVault administration adjudicates the dispute and reverses funds to store credit or payment origin.</p>
          </div>
        )}

        {currentTab === 'rules' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Marketplace Code of Conduct</h2>
            <p>To preserve integrity and trust, all buyers and vendors must abide by these rules:</p>
            <ul className="list-disc pl-5 space-y-2 text-brand-lightSecText">
              <li><strong>Zero External Transactions:</strong> Soliciting off-platform payment via Telegram, Discord, or external crypto wallets results in immediate permanent ban and forfeiture of pending balances.</li>
              <li><strong>Accurate Listing Specs:</strong> Listings must accurately declare account ranks, regional locks, and access status.</li>
              <li><strong>Civil Communication:</strong> Harassment, threats, or abusive language in chat or ticket threads will not be tolerated.</li>
            </ul>
          </div>
        )}

        {currentTab === 'abuse' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Abuse & Intellectual Property Notice</h2>
            <p>GameVault respects intellectual property rights and takes prompt action against illicit conduct.</p>
            <p>If you believe a listing violates copyright, contains unauthorized software/cheats, or infringes on trademark guidelines, submit a takedown notice to: <strong className="text-brand-blue">abuse@gamevault.gg</strong>.</p>
          </div>
        )}
      </div>
    </div>
  );
};
